import React from "react";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import { TextField } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import { useTheme } from "@material-ui/core/styles";
import { i18n } from "../../../translate/i18n";

const MediaPreviewPanel = (props) => {
  const {
    medias,
    activeMediaIndex,
    handleSelectActiveMedia,
    handleRemoveMedia,
    uploadProgress,
    loading,
    recording,
    ticketStatus,
    handleChangeMedias,
    isDragOver,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    getFileType,
    mediaCaptions,
    mediaCaptionInputRef,
    setMediaCaptions,
    handleMediaCaptionPaste,
    handleCancelAll,
    handleUploadMedia,
  } = props;

  const theme = useTheme();
  const isDark = theme.mode === "dark";
  const activeMedia = medias[activeMediaIndex];
  const isImage = activeMedia?.type.startsWith("image/");
  const isVideo = activeMedia?.type.startsWith("video/");

  return (
    <Paper
      elevation={0}
      square
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 400,
        display: "flex",
        flexDirection: "column",
        backgroundColor: isDark ? "#111827" : "#ffffff",
      }}
    >
      {/* Input oculto para "+" */}
      <input
        multiple
        type="file"
        id="upload-input-preview"
        disabled={loading || recording || ticketStatus !== "open"}
        onChange={handleChangeMedias}
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        style={{ display: "none" }}
      />

      {/* ÁREA PRINCIPAL: vista previa grande */}
      <div
        style={{
          flex: 1,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
          overflow: "hidden",
        }}
      >
        {/* Botón cerrar — top left */}
        <IconButton
          onClick={handleCancelAll}
          size="small"
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 10,
            backgroundColor: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.12)",
            color: isDark ? "#fff" : "#333",
            width: 36,
            height: 36,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Vista previa del archivo activo */}
        {activeMedia && (
          isImage ? (
            <img
              src={URL.createObjectURL(activeMedia)}
              alt={activeMedia.name}
              style={{
                maxWidth: "88%",
                maxHeight: "88%",
                objectFit: "contain",
                borderRadius: 6,
                boxShadow: "0 4px 32px rgba(0,0,0,0.22)",
              }}
            />
          ) : isVideo ? (
            <video
              src={URL.createObjectURL(activeMedia)}
              controls
              style={{
                maxWidth: "88%",
                maxHeight: "88%",
                borderRadius: 6,
                boxShadow: "0 4px 32px rgba(0,0,0,0.22)",
              }}
            />
          ) : (
            <div
              style={{
                textAlign: "center",
                color: isDark ? "#d1d5db" : "#555",
                padding: 24,
              }}
            >
              <div style={{ fontSize: 80, lineHeight: 1, marginBottom: 16 }}>
                {getFileType(activeMedia)}
              </div>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 600,
                  color: isDark ? "#f9fafb" : "#1f2937",
                  marginBottom: 6,
                  wordBreak: "break-all",
                }}
              >
                {activeMedia.name.length > 48
                  ? activeMedia.name.substring(0, 48) + "…"
                  : activeMedia.name}
              </div>
              <div style={{ fontSize: 13, color: isDark ? "#9ca3af" : "#888" }}>
                {(activeMedia.size / (1024 * 1024)).toFixed(1)} MB
              </div>
            </div>
          )
        )}

        {/* Overlay drag-over cuando hay archivos en preview */}
        {isDragOver && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(37, 211, 102, 0.13)",
              border: "3px dashed #25D366",
              zIndex: 20,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                textAlign: "center",
                color: "#25D366",
                userSelect: "none",
              }}
            >
              <div style={{ fontSize: 52, lineHeight: 1, marginBottom: 10 }}>
                📎
              </div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                Suelta para agregar más
              </div>
            </div>
          </div>
        )}
      </div>

      {/* BARRA INFERIOR: dos filas — caption arriba, thumbnails+enviar abajo */}
      <div
        style={{
          flexShrink: 0,
          backgroundColor: isDark ? "#111827" : "#ffffff",
          borderTop: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
        }}
      >
        {/* Fila 1: Caption input — ancho completo */}
        <div style={{ padding: "10px 14px 6px 14px" }}>
          <TextField
            inputRef={mediaCaptionInputRef}
            fullWidth
            multiline
            minRows={1}
            maxRows={3}
            variant="outlined"
            size="small"
            placeholder={i18n.t("messagesInput.mediaCaptionPlaceholder")}
            value={mediaCaptions[activeMediaIndex] ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setMediaCaptions((prev) => {
                const next = [...prev];
                while (next.length <= activeMediaIndex) next.push("");
                next[activeMediaIndex] = v;
                return next;
              });
            }}
            onPaste={handleMediaCaptionPaste}
            disabled={loading || recording || ticketStatus !== "open"}
            InputProps={{
              style: {
                backgroundColor: isDark ? "#1f2937" : "#f3f4f6",
                color: isDark ? "#f9fafb" : "#1f2937",
                fontSize: 14,
                borderRadius: 24,
              },
            }}
          />
        </div>

        {/* Fila 2: Strip de thumbnails centrado + send anclado derecha */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "4px 14px 10px 14px",
            minHeight: 70,
          }}
        >
        {/* Strip horizontal de miniaturas + "+" — centrado */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            overflowX: "auto",
            paddingBottom: 2,
          }}
        >
          {medias.map((media, index) => (
            <div
              key={index}
              onClick={() => handleSelectActiveMedia(index)}
              style={{
                position: "relative",
                width: 54,
                height: 54,
                flexShrink: 0,
                borderRadius: 8,
                overflow: "visible",
                cursor: "pointer",
                border:
                  index === activeMediaIndex
                    ? "2px solid #25d366"
                    : `2px solid ${isDark ? "#4B5563" : "#d1d5db"}`,
                boxShadow:
                  index === activeMediaIndex
                    ? "0 0 0 3px rgba(37,211,102,0.28)"
                    : "none",
                backgroundColor: isDark ? "#374151" : "#f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 6,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {media.type.startsWith("image/") ? (
                  <img
                    src={URL.createObjectURL(media)}
                    alt={media.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div style={{ fontSize: 24 }}>{getFileType(media)}</div>
                )}
              </div>

              {/* Barra de progreso */}
              {uploadProgress[index] !== undefined && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    backgroundColor: "rgba(0,0,0,0.15)",
                    borderRadius: "0 0 6px 6px",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      backgroundColor: "#25d366",
                      width: `${uploadProgress[index]}%`,
                      transition: "width 0.3s",
                      borderRadius: "0 0 6px 6px",
                    }}
                  />
                </div>
              )}

              {/* Botón eliminar */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveMedia(index);
                }}
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  backgroundColor: "#ef4444",
                  color: "#fff",
                  width: 18,
                  height: 18,
                  padding: 0,
                  zIndex: 5,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                }}
              >
                <CloseIcon style={{ fontSize: 11 }} />
              </IconButton>
            </div>
          ))}

          {/* Botón "+" agregar más archivos */}
          <div
            onClick={() =>
              document.getElementById("upload-input-preview")?.click()
            }
            style={{
              width: 54,
              height: 54,
              flexShrink: 0,
              borderRadius: 8,
              border: `2px dashed ${isDark ? "#6B7280" : "#d1d5db"}`,
              backgroundColor: isDark ? "#1f2937" : "#f9fafb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: 26,
              fontWeight: 300,
              color: isDark ? "#9ca3af" : "#6b7280",
              transition: "background-color 0.15s",
            }}
          >
            +
          </div>
        </div>

          {/* Botón enviar — anclado al borde derecho */}
          <IconButton
            onClick={handleUploadMedia}
            disabled={loading || medias.length === 0}
            style={{
              position: "absolute",
              right: 14,
              backgroundColor:
                loading || medias.length === 0 ? "#9ca3af" : "#25d366",
              color: "#fff",
              width: 54,
              height: 54,
              flexShrink: 0,
              borderRadius: "50%",
              boxShadow:
                loading || medias.length === 0
                  ? "none"
                  : "0 2px 14px rgba(37,211,102,0.45)",
              transition: "background-color 0.2s, box-shadow 0.2s",
            }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </IconButton>
        </div>
      </div>
    </Paper>
  );
};

export default MediaPreviewPanel;
